/**
 * Screenshot Upload Field Component
 *
 * Supports file upload with preview, validation, and clear/replace functionality.
 * Uses shadcn/ui components (NOT Ant Design).
 *
 * Module: 3.0 - System Operations (Issues Manager Phase 3 - Week 2)
 * PRD: docs/prds/PRD-ISSUES-MANAGER.md v2.1
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { useDropzone, FileRejection } from "react-dropzone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { X, Upload, ImageIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface ScreenshotUploadFieldProps {
  value: File | File[] | null;
  onChange: (files: File | File[] | null) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  maxFiles?: number;
}

const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25MB for all files combined

export function ScreenshotUploadField({
  value,
  onChange,
  label = "Screenshot",
  required = false,
  disabled = false,
  multiple = false,
  maxFiles = 5,
}: ScreenshotUploadFieldProps) {
  const files = Array.isArray(value) ? value : value ? [value] : [];
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Cleanup preview URLs on unmount or when files change
  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  const validateFile = (file: File): boolean => {
    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PNG, JPG, JPEG, or WebP file.",
        variant: "destructive",
      });
      return false;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "File too large",
        description: `File size must be less than ${MAX_FILE_SIZE / 1024 / 1024}MB.`,
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const newFiles = Array.from(selectedFiles);

    // Validate each file
    const validFiles: File[] = [];
    for (const file of newFiles) {
      if (validateFile(file)) {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) {
      // Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Check max files limit
    const combinedFiles = multiple ? [...files, ...validFiles] : validFiles;
    if (combinedFiles.length > maxFiles) {
      toast({
        title: "Too many files",
        description: `Maximum ${maxFiles} files allowed. Only the first ${maxFiles} will be used.`,
        variant: "destructive",
      });
      combinedFiles.splice(maxFiles);
    }

    // Check total size limit
    const totalSize = combinedFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > MAX_TOTAL_SIZE) {
      toast({
        title: "Total file size too large",
        description: `Combined file size must be less than ${MAX_TOTAL_SIZE / 1024 / 1024}MB.`,
        variant: "destructive",
      });
      // Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Revoke old preview URLs
    previewUrls.forEach((url) => URL.revokeObjectURL(url));

    // Create new preview URLs
    const newPreviewUrls = combinedFiles.map((file) => URL.createObjectURL(file));
    setPreviewUrls(newPreviewUrls);

    // Update parent
    onChange(multiple ? combinedFiles : combinedFiles[0]);

    // Clear the file input to allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemove = (index: number) => {
    // Revoke the specific preview URL
    URL.revokeObjectURL(previewUrls[index]);

    // Remove file and preview URL
    const newFiles = files.filter((_, i) => i !== index);
    const newPreviewUrls = previewUrls.filter((_, i) => i !== index);

    setPreviewUrls(newPreviewUrls);

    // Update parent
    if (newFiles.length === 0) {
      onChange(null);
    } else if (multiple) {
      onChange(newFiles);
    } else {
      onChange(newFiles[0]);
    }

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClearAll = () => {
    // Revoke all preview URLs
    previewUrls.forEach((url) => URL.revokeObjectURL(url));

    // Clear state
    setPreviewUrls([]);
    onChange(null);

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAddMore = () => {
    // Trigger file input click
    fileInputRef.current?.click();
  };

  // Drag-and-drop handler
  const handleDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      // Show errors for rejected files
      if (rejectedFiles.length > 0) {
        rejectedFiles.forEach(({ file, errors }) => {
          toast({
            title: `${file.name} rejected`,
            description: errors.map((e) => e.message).join(", "),
            variant: "destructive",
          });
        });
      }

      if (acceptedFiles.length === 0) return;

      // Validate accepted files
      const validFiles: File[] = [];
      for (const file of acceptedFiles) {
        if (validateFile(file)) {
          validFiles.push(file);
        }
      }

      if (validFiles.length === 0) return;

      // Check max files limit
      const combinedFiles = multiple ? [...files, ...validFiles] : validFiles;
      if (combinedFiles.length > maxFiles) {
        toast({
          title: "Too many files",
          description: `Maximum ${maxFiles} files allowed. Only the first ${maxFiles} will be used.`,
          variant: "destructive",
        });
        combinedFiles.splice(maxFiles);
      }

      // Check total size limit
      const totalSize = combinedFiles.reduce((sum, file) => sum + file.size, 0);
      if (totalSize > MAX_TOTAL_SIZE) {
        toast({
          title: "Total file size too large",
          description: `Combined file size must be less than ${MAX_TOTAL_SIZE / 1024 / 1024}MB.`,
          variant: "destructive",
        });
        return;
      }

      // Revoke old preview URLs
      previewUrls.forEach((url) => URL.revokeObjectURL(url));

      // Create new preview URLs
      const newPreviewUrls = combinedFiles.map((file) => URL.createObjectURL(file));
      setPreviewUrls(newPreviewUrls);

      // Update parent
      onChange(multiple ? combinedFiles : combinedFiles[0]);
    },
    [files, multiple, maxFiles, previewUrls, onChange, toast]
  );

  // Setup dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/webp": [".webp"],
    },
    multiple,
    disabled,
    maxSize: MAX_FILE_SIZE,
    maxFiles: multiple ? maxFiles : 1,
    noClick: files.length > 0, // Disable click when files are already selected
    noKeyboard: files.length > 0,
  });

  return (
    <div className="space-y-2">
      {/* Label */}
      <Label htmlFor="screenshot-upload">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>

      {/* File Input (Hidden) */}
      <Input
        ref={fileInputRef}
        id="screenshot-upload"
        type="file"
        accept={ALLOWED_TYPES.join(",")}
        onChange={handleFileChange}
        disabled={disabled}
        multiple={multiple}
        className="hidden"
      />

      {/* Drag-and-Drop Zone or File Preview */}
      {files.length === 0 ? (
        <div
          {...getRootProps()}
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200",
            isDragActive && "border-primary bg-primary/5 scale-[1.02]",
            !isDragActive && "border-border hover:border-primary/50",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        >
          <input {...getInputProps()} />
          <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          {isDragActive ? (
            <p className="text-primary font-medium">Drop screenshots here...</p>
          ) : (
            <>
              <p className="font-medium text-foreground mb-1">
                Drag & drop screenshot{multiple ? "s" : ""} here
              </p>
              <p className="text-sm text-muted-foreground">
                or click to browse
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Progress Indicator (for multiple files) */}
          {multiple && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {files.length} of {maxFiles} files selected
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                disabled={disabled}
              >
                Clear All
              </Button>
            </div>
          )}

          {/* File List */}
          <div className="space-y-2">
            {files.map((file, index) => (
              <Card key={`${file.name}-${index}`}>
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    {/* File Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <ImageIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <p className="text-sm font-medium truncate">{file.name}</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(index)}
                        disabled={disabled}
                        className="flex-shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Preview Image */}
                    {previewUrls[index] && (
                      <img
                        src={previewUrls[index]}
                        alt={`Screenshot preview ${index + 1}`}
                        className="w-full max-w-md rounded-md border border-border"
                      />
                    )}

                    {/* File Details */}
                    <div className="text-xs text-muted-foreground">
                      <p>Size: {(file.size / 1024).toFixed(2)} KB</p>
                      <p>Type: {file.type}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Add More Button (for multiple files) */}
          {multiple && files.length < maxFiles && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddMore}
              disabled={disabled}
              className="w-full"
            >
              <Upload className="w-4 h-4 mr-2" />
              Add More Screenshots ({files.length}/{maxFiles})
            </Button>
          )}
        </div>
      )}

      {/* Validation Hint */}
      <p className="text-xs text-muted-foreground">
        Accepted formats: PNG, JPG, JPEG, WebP (max {MAX_FILE_SIZE / 1024 / 1024}MB per file
        {multiple ? `, ${MAX_TOTAL_SIZE / 1024 / 1024}MB total` : ""})
      </p>
    </div>
  );
}
